import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { 
  LayoutDashboard, 
  Home, 
  Users, 
  Wrench, 
  FileText, 
  BarChart3,
  Bell,
  Plus,
  Search,
  FilePlus
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
 import { mockProperties, mockTenants, mockMaintenance, PropertyType, Property, Tenant } from "@/lib/mock-data";
import { TenantProfile } from "@/components/admin/TenantProfile";
import { ContractTemplate } from "@/components/admin/ContractTemplate";

export const Route = createFileRoute("/dashboard")({
  component: DashboardPage,
});

function DashboardPage() {
  const [activeTab, setActiveTab] = useState("properties");
   const [properties, setProperties] = useState<Property[]>(mockProperties);
   const [tenants, setTenants] = useState<Tenant[]>(mockTenants);
   const [selectedTenant, setSelectedTenant] = useState<Tenant>(mockTenants[0]);
   const [selectedProperty, setSelectedProperty] = useState<Property>(mockProperties[0]);
   const [isPropertyDialogOpen, setIsPropertyDialogOpen] = useState(false);
   const [newProperty, setNewProperty] = useState<Partial<Property>>({
     type: 'casa',
     status: 'disponivel',
     value: 0
   });

  const getPropertyTypeColor = (type: PropertyType) => {
    switch (type) {
      case 'casa': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'loja': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'combo': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'luxo': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50/50">
      {/* Sidebar */}
      <aside className="w-64 border-r bg-white p-6 hidden md:block">
        <div className="flex items-center gap-2 mb-8 px-2">
          <div className="h-8 w-8 bg-primary rounded-lg flex items-center justify-center text-white font-bold">L</div>
          <h1 className="font-display font-bold text-xl">Lady Gestão</h1>
        </div>
        
        <nav className="space-y-1">
          <NavItem icon={<LayoutDashboard size={20} />} label="Início" active={activeTab === 'overview'} onClick={() => setActiveTab('properties')} />
          <NavItem icon={<Home size={20} />} label="Propriedades" active={activeTab === 'properties'} onClick={() => setActiveTab('properties')} />
          <NavItem icon={<Users size={20} />} label="Locatários" active={activeTab === 'tenants'} onClick={() => setActiveTab('tenants')} />
          <NavItem icon={<Wrench size={20} />} label="Manutenção" active={activeTab === 'maintenance'} onClick={() => setActiveTab('maintenance')} />
          <NavItem icon={<FileText size={20} />} label="Contratos" active={activeTab === 'contracts'} onClick={() => setActiveTab('contracts')} />
          <NavItem icon={<BarChart3 size={20} />} label="Relatórios" />
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        <header className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Painel de Controle</h2>
            <p className="text-muted-foreground">Bem-vinda de volta, Lady. Aqui está o resumo do seu império.</p>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" className="relative">
              <Bell size={20} />
              <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full border-2 border-white"></span>
            </Button>
           <div className="flex gap-2">
             <Dialog open={isPropertyDialogOpen} onOpenChange={setIsPropertyDialogOpen}>
               <DialogTrigger asChild>
                 <Button className="gap-2">
                   <Plus size={18} /> Cadastrar Imóvel
                 </Button>
               </DialogTrigger>
               <DialogContent className="sm:max-w-[425px]">
                 <DialogHeader>
                   <DialogTitle>Novo Cadastro de Imóvel</DialogTitle>
                 </DialogHeader>
                 <div className="grid gap-4 py-4">
                   <div className="grid gap-2">
                     <label className="text-sm font-medium">Nome do Imóvel</label>
                     <Input 
                       placeholder="Ex: Casa Jardim Primavera" 
                       value={newProperty.name || ''}
                       onChange={(e) => setNewProperty({...newProperty, name: e.target.value})}
                     />
                   </div>
                   <div className="grid gap-2">
                     <label className="text-sm font-medium">Tipo</label>
                     <select 
                       className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                       value={newProperty.type}
                       onChange={(e) => setNewProperty({...newProperty, type: e.target.value as PropertyType})}
                     >
                       <option value="casa">Casa</option>
                       <option value="loja">Loja</option>
                       <option value="combo">Posto/Mercado (Combo)</option>
                       <option value="luxo">Médio/Alto Luxo</option>
                     </select>
                   </div>
                   <div className="grid gap-2">
                     <label className="text-sm font-medium">Valor do Aluguel (R$)</label>
                     <Input 
                       type="number" 
                       placeholder="0,00" 
                       value={newProperty.value}
                       onChange={(e) => setNewProperty({...newProperty, value: Number(e.target.value)})}
                     />
                   </div>
                   <div className="grid gap-2">
                     <label className="text-sm font-medium">Endereço Completo</label>
                     <Input 
                       placeholder="Rua, Número, Bairro, Cidade" 
                       value={newProperty.address || ''}
                       onChange={(e) => setNewProperty({...newProperty, address: e.target.value})}
                     />
                   </div>
                   <Button 
                     className="mt-2"
                     onClick={() => {
                       if (newProperty.name && newProperty.address && newProperty.value) {
                         const property: Property = {
                           id: Math.random().toString(36).substr(2, 9),
                           name: newProperty.name,
                           type: newProperty.type as PropertyType,
                           value: newProperty.value,
                           address: newProperty.address,
                           status: 'disponivel'
                         };
                         setProperties([...properties, property]);
                         setNewProperty({ type: 'casa', status: 'disponivel', value: 0 });
                         setIsPropertyDialogOpen(false);
                       }
                     }}
                   >
                     Salvar Cadastro
                   </Button>
                 </div>
               </DialogContent>
             </Dialog>
           </div>
          </div>
        </header>

        {/* Stats Summary */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <StatCard title="Receita Total" value="R$ 27.500" description="+12% em relação ao mês anterior" />
           <StatCard title="Propriedades" value={properties.length.toString()} description={`${properties.filter(p => p.status === 'alugada').length} Alugadas, ${properties.filter(p => p.status === 'disponivel').length} Disponíveis`} />
          <StatCard title="Manutenções" value={mockMaintenance.length.toString()} description="1 Pendente" />
          <StatCard title="Taxa de Ocupação" value="80%" description="Excelente desempenho" />
        </div>

        <Tabs defaultValue="properties" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-white border p-1 h-auto flex-wrap sm:flex-nowrap">
            <TabsTrigger value="properties" className="flex-1 sm:flex-none">Propriedades</TabsTrigger>
            <TabsTrigger value="tenants" className="flex-1 sm:flex-none">Locatários</TabsTrigger>
            <TabsTrigger value="maintenance" className="flex-1 sm:flex-none">Chamados</TabsTrigger>
            <TabsTrigger value="contracts" className="flex-1 sm:flex-none">Contratos</TabsTrigger>
          </TabsList>

          <TabsContent value="properties" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
               {properties.map(property => (
                <Card key={property.id} className="overflow-hidden border-2 hover:border-primary/50 transition-all">
                  <div className={"h-2 " + getPropertyTypeColor(property.type).split(' ')[0]}></div>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-lg font-bold">{property.name}</CardTitle>
                    <Badge variant="outline" className={getPropertyTypeColor(property.type)}>
                      {property.type.toUpperCase()}
                    </Badge>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-primary">R$ {property.value.toLocaleString('pt-BR')}</div>
                    <p className="text-sm text-muted-foreground mt-1">{property.address}</p>
                    <div className="mt-4 flex justify-between items-center">
                      <Badge className={property.status === 'alugada' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {property.status}
                      </Badge>
                      <Button variant="ghost" size="sm">Ver detalhes</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="tenants" className="space-y-4">
             <div className="grid gap-6 md:grid-cols-3">
               <Card className="md:col-span-1">
                 <CardHeader>
                   <CardTitle className="text-lg">Lista de Locatários</CardTitle>
                 </CardHeader>
                 <CardContent className="p-0">
                     {tenants.map(t => (
                      <button 
                        key={t.id} 
                        onClick={() => setSelectedTenant(t)}
                        className={"w-full text-left p-4 border-b hover:bg-slate-50 transition-colors " + (selectedTenant.id === t.id ? 'bg-slate-100' : '')}
                      >
                        <p className="font-bold">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.whatsapp}</p>
                      </button>
                    ))}
                 </CardContent>
               </Card>
               <Card className="md:col-span-2">
                 <CardHeader>
                   <CardTitle className="text-lg">Detalhes do Locatário</CardTitle>
                 </CardHeader>
                 <CardContent>
                    <TenantProfile tenant={selectedTenant} />
                 </CardContent>
               </Card>
             </div>
          </TabsContent>

          <TabsContent value="maintenance">
             <div className="flex justify-between items-center mb-4">
               <h3 className="text-xl font-bold">Chamados de Manutenção</h3>
               <Dialog>
                 <DialogTrigger asChild>
                   <Button variant="outline" className="gap-2"><Plus size={16} /> Abrir Chamado</Button>
                 </DialogTrigger>
                 <DialogContent>
                   <DialogHeader>
                     <DialogTitle>Relatar Problema</DialogTitle>
                   </DialogHeader>
                   <div className="space-y-4 py-4">
                     <div className="grid grid-cols-2 gap-2">
                       <Button variant="outline" className="justify-start gap-2 h-auto py-3">
                         <div className="w-2 h-2 rounded-full bg-yellow-400"></div> Falta de Luz
                       </Button>
                       <Button variant="outline" className="justify-start gap-2 h-auto py-3">
                         <div className="w-2 h-2 rounded-full bg-blue-400"></div> Problema com Água
                       </Button>
                       <Button variant="destructive" className="justify-start gap-2 h-auto py-3">
                         <div className="w-2 h-2 rounded-full bg-white"></div> Emergência
                       </Button>
                       <Button variant="outline" className="justify-start gap-2 h-auto py-3">
                         <div className="w-2 h-2 rounded-full bg-slate-400"></div> Outro
                       </Button>
                     </div>
                     <Input placeholder="Observações..." />
                     <Button className="w-full">Enviar Notificação</Button>
                   </div>
                 </DialogContent>
               </Dialog>
             </div>
             <Card>
               <CardContent className="pt-6">
                 <div className="space-y-4">
                   {mockMaintenance.map(req => (
                     <div key={req.id} className="flex items-center justify-between p-4 border rounded-lg bg-white shadow-sm">
                       <div className="flex items-center gap-4">
                         <div className={"p-2 rounded-full " + (req.type === 'emergencia' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600')}>
                            <Wrench size={20} />
                         </div>
                         <div>
                           <p className="font-semibold">{req.description}</p>
                           <p className="text-xs text-muted-foreground">Propriedade: {mockProperties.find(p => p.id === req.propertyId)?.name} • {new Date(req.createdAt).toLocaleDateString()}</p>
                         </div>
                       </div>
                       <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 uppercase tracking-tight">Pendente</Badge>
                     </div>
                   ))}
                 </div>
               </CardContent>
             </Card>
          </TabsContent>

          <TabsContent value="contracts">
            <div className="grid gap-6 md:grid-cols-4">
              <Card className="md:col-span-1">
                <CardHeader>
                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Selecionar Dados</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-xs font-medium">Locatário</label>
                    <select 
                      className="w-full p-2 border rounded-md text-sm"
                       value={selectedTenant?.id}
                       onChange={(e) => setSelectedTenant(tenants.find(t => t.id === e.target.value) || tenants[0])}
                    >
                      {mockTenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium">Imóvel</label>
                    <select 
                      className="w-full p-2 border rounded-md text-sm"
                       value={selectedProperty?.id}
                       onChange={(e) => setSelectedProperty(properties.find(p => p.id === e.target.value) || properties[0])}
                    >
                      {mockProperties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <Button className="w-full gap-2" variant="outline"><FilePlus size={16} /> Gerar Novo Modelo</Button>
                </CardContent>
              </Card>
              <div className="md:col-span-3">
                <ContractTemplate tenant={selectedTenant} property={selectedProperty} />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function NavItem({ icon, label, active = false, onClick }: { icon: React.ReactNode, label: string, active?: boolean, onClick?: () => void }) {
  return (
    <button 
      onClick={onClick}
      className={"w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors " + (
        active 
          ? "bg-primary text-primary-foreground" 
          : "text-muted-foreground hover:bg-slate-100 hover:text-foreground"
      )}
    >
      {icon}
      <span className="font-medium text-sm">{label}</span>
    </button>
  );
}

function StatCard({ title, value, description }: { title: string, value: string, description: string }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </CardContent>
    </Card>
  );
}
